"use client";

import { cn } from "@/lib/utils";
import type { SnookerTemplateData } from "../snooker-template";
import { ScorecardShell, type ScorecardVariant } from "./scorecard-shell";

type ScorecardParticipant = {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  username: string | null;
};

type SnookerScorecardProps = {
  data: SnookerTemplateData | null;
  participants: ScorecardParticipant[];
  variant?: ScorecardVariant;
};

export function SnookerScorecard({
  data,
  participants,
  variant = "feed",
}: SnookerScorecardProps) {
  if (!data) return null;

  const yourTeam = participants.filter(
    (p) => data.positions?.[p.id] === "your-team",
  );
  const opponentTeam = participants.filter(
    (p) => data.positions?.[p.id] === "opponent-team",
  );

  const showScore = data.hasScore !== false;
  if (!showScore) return null;

  const yourWon = data.matchScore.yourFrames > data.matchScore.opponentFrames;
  const opponentWon =
    data.matchScore.opponentFrames > data.matchScore.yourFrames;
  const frameDetails = data.frames ?? [];

  const yourName =
    yourTeam.length > 1
      ? "Team 1"
      : (yourTeam[0]?.displayName?.split(" ")[0] ?? "Team 1");
  const oppName =
    opponentTeam.length > 1
      ? "Team 2"
      : (opponentTeam[0]?.displayName?.split(" ")[0] ?? "Team 2");

  return (
    <ScorecardShell variant={variant}>
      <div className="flex items-center justify-between">
        <div className="flex flex-col items-start">
          <span className="text-sm font-semibold text-text-primary">
            {yourName}
          </span>
          {yourWon && (
            <span className="text-xs font-medium text-brand">Winner</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "text-2xl font-bold",
              yourWon ? "text-brand" : "text-text-primary",
            )}
          >
            {data.matchScore.yourFrames}
          </span>
          <span className="text-text-secondary">–</span>
          <span
            className={cn(
              "text-2xl font-bold",
              opponentWon ? "text-error" : "text-text-primary",
            )}
          >
            {data.matchScore.opponentFrames}
          </span>
        </div>
        <div className="flex flex-col items-end">
          <span className="text-sm font-semibold text-text-primary">
            {oppName}
          </span>
          {opponentWon && (
            <span className="text-xs font-medium text-error">Winner</span>
          )}
        </div>
      </div>

      {frameDetails.length > 0 && (
        <div className="mt-3 flex flex-wrap justify-center gap-1">
          {frameDetails.slice(0, 12).map((frame, index) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: static score display
              key={`${index}-${frame.yourScore}-${frame.opponentScore}`}
              className="text-2xs rounded-full bg-surface-alt px-2 py-1 text-text-secondary"
            >
              F{index + 1} {frame.yourScore}–{frame.opponentScore}
            </span>
          ))}
          {frameDetails.length > 12 && (
            <span className="text-2xs rounded-full bg-surface-alt px-2 py-1 text-text-secondary">
              +{frameDetails.length - 12} more
            </span>
          )}
        </div>
      )}

      {data.highestBreak && data.highestBreak > 0 && (
        <div className="mt-2 border-t border-border pt-2 text-center">
          <span className="text-sm text-text-secondary">Highest Break: </span>
          <span className="font-bold text-brand">{data.highestBreak}</span>
          {data.highestBreak >= 100 && <span className="ml-1">🎱</span>}
        </div>
      )}
    </ScorecardShell>
  );
}
